import { spaceMessageSearchQuerySchema } from "./space-message-search.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/list-signals-query.ts
const CATCHUP_DEFAULT_LIMIT = 200;
const CATCHUP_MAX_LIMIT = 500;
const MAX_DATE_TIMESTAMP_MS = 864e13;
const NON_NEGATIVE_INT_RE = /^\d+$/;
const SIGNAL_TYPE_SET = /* @__PURE__ */ new Set([
	"signal.text",
	"signal.status",
	"signal.system_notice"
]);
const listSignalsQueryInputSchema = z$1.strictObject({
	q: spaceMessageSearchQuerySchema.shape.q.nullable().optional(),
	afterTs: z$1.string().nullable(),
	cursor: z$1.string().nullable(),
	limit: z$1.string().nullable(),
	recent: z$1.string().nullable(),
	afterSignalId: z$1.string().nullable(),
	aroundSignalId: z$1.string().nullable(),
	beforeCount: z$1.string().nullable(),
	afterCount: z$1.string().nullable(),
	fromProfileIds: z$1.array(z$1.string()),
	signalTypes: z$1.array(z$1.string()),
	hasLimit: z$1.boolean()
});
function readListSignalsQueryRawValues(searchParams) {
	return {
		...searchParams.has("q") ? { q: searchParams.get("q") } : {},
		afterTs: searchParams.get("afterTs"),
		cursor: searchParams.get("cursor"),
		limit: searchParams.get("limit"),
		recent: searchParams.get("recent"),
		afterSignalId: searchParams.get("afterSignalId"),
		aroundSignalId: searchParams.get("aroundSignalId"),
		beforeCount: searchParams.get("beforeCount"),
		afterCount: searchParams.get("afterCount"),
		fromProfileIds: searchParams.getAll("fromProfileId"),
		signalTypes: searchParams.getAll("signalType"),
		hasLimit: searchParams.has("limit")
	};
}
const parseOptionalNonNegativeInt = (value) => {
	if (value === null) return null;
	const normalized = value.trim();
	if (normalized === "") return null;
	if (!NON_NEGATIVE_INT_RE.test(normalized)) return null;
	const parsed = Number.parseInt(normalized, 10);
	if (!Number.isFinite(parsed) || parsed < 0) return null;
	return parsed;
};
const hasNonEmptyQueryValue = (value) => value !== null && value.trim() !== "";
/** `null` and `0` both map to default page size; other values are capped. */
const parseLimit = (parsed) => {
	if (parsed === null || parsed === 0) return CATCHUP_DEFAULT_LIMIT;
	return Math.min(parsed, CATCHUP_MAX_LIMIT);
};
const normalizeCursor = (value) => {
	const cursor = String(value ?? "").trim();
	return cursor.length > 0 ? cursor : null;
};
const normalizeIdentifier = (value) => {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
};
const parseRecentQuery = (value) => {
	const normalized = String(value ?? "").trim().toLowerCase();
	return normalized === "1" || normalized === "true";
};
const normalizeUniqueStrings = (values) => {
	const deduped = /* @__PURE__ */ new Set();
	for (const value of values) {
		const normalized = String(value ?? "").trim();
		if (normalized.length > 0) deduped.add(normalized);
	}
	return [...deduped];
};
const normalizeSignalTypes = (values) => {
	const deduped = /* @__PURE__ */ new Set();
	for (const value of values) {
		const normalized = String(value ?? "").trim();
		if (SIGNAL_TYPE_SET.has(normalized)) deduped.add(normalized);
	}
	return [...deduped];
};
const addQueryIssue = (ctx, path, message) => {
	ctx.addIssue({
		code: "custom",
		message,
		path: [path]
	});
};
const buildListSignalsQueryValidationState = (value) => ({
	afterTs: parseOptionalNonNegativeInt(value.afterTs),
	parsedLimit: parseOptionalNonNegativeInt(value.limit),
	afterSignalId: normalizeIdentifier(value.afterSignalId),
	aroundSignalId: normalizeIdentifier(value.aroundSignalId),
	beforeCount: parseOptionalNonNegativeInt(value.beforeCount),
	afterCount: parseOptionalNonNegativeInt(value.afterCount),
	recentOnly: parseRecentQuery(value.recent),
	cursor: normalizeCursor(value.cursor)
});
function resolveListSignalsReadQuerySemantics(input) {
	const issues = [];
	if (input.afterSignalId && input.aroundSignalId) issues.push({
		path: "afterSignalId",
		message: "Cannot use both afterSignalId and aroundSignalId."
	});
	if (input.recentOnly && (input.afterSignalId || input.aroundSignalId)) issues.push({
		path: "recent",
		message: "Recent history mode cannot be combined with signal anchors."
	});
	if ((input.beforeCount !== null || input.afterCount !== null) && !input.aroundSignalId) issues.push({
		path: input.beforeCount === null ? "afterCount" : "beforeCount",
		message: "beforeCount and afterCount require aroundSignalId."
	});
	if (input.aroundSignalId && input.hasExplicitLimit) issues.push({
		path: "aroundSignalId",
		message: "aroundSignalId cannot be combined with limit."
	});
	if (issues.length > 0) return {
		ok: false,
		issues
	};
	return {
		ok: true,
		value: {
			recentOnly: input.recentOnly,
			includeLimit: input.hasExplicitLimit,
			limit: input.afterSignalId && input.limit === 0 ? 0 : parseLimit(input.limit),
			afterSignalId: input.afterSignalId,
			aroundSignalId: input.aroundSignalId,
			beforeCount: input.beforeCount,
			afterCount: input.afterCount
		}
	};
}
function normalizeListSignalsReadQuerySemantics(input) {
	const result = resolveListSignalsReadQuerySemantics(input);
	if (!result.ok) throw new Error(result.issues[0]?.message ?? "Invalid list signals query.");
	return result.value;
}
const validateListSignalsPrimitiveValues = (value, state, ctx) => {
	if (hasNonEmptyQueryValue(value.afterTs) && (state.afterTs === null || state.afterTs > MAX_DATE_TIMESTAMP_MS)) addQueryIssue(ctx, "afterTs", "Invalid afterTs");
	if (hasNonEmptyQueryValue(value.limit) && state.parsedLimit === null) addQueryIssue(ctx, "limit", "Invalid limit");
	if (value.afterSignalId !== null && state.afterSignalId === null) addQueryIssue(ctx, "afterSignalId", "Invalid afterSignalId");
	if (value.aroundSignalId !== null && state.aroundSignalId === null) addQueryIssue(ctx, "aroundSignalId", "Invalid aroundSignalId");
	if (hasNonEmptyQueryValue(value.beforeCount) && state.beforeCount === null) addQueryIssue(ctx, "beforeCount", "Invalid beforeCount");
	if (hasNonEmptyQueryValue(value.afterCount) && state.afterCount === null) addQueryIssue(ctx, "afterCount", "Invalid afterCount");
};
const validateListSignalsSignalTypes = (signalTypes, ctx) => {
	for (const signalType of signalTypes) {
		const normalized = signalType.trim();
		if (normalized.length === 0 || !SIGNAL_TYPE_SET.has(normalized)) {
			addQueryIssue(ctx, "signalTypes", "Invalid signalType");
			break;
		}
	}
};
const validateListSignalsQueryCombinations = (value, state, ctx) => {
	if ((state.afterSignalId || state.aroundSignalId) && (state.afterTs !== null || state.cursor !== null)) addQueryIssue(ctx, state.afterSignalId ? "afterSignalId" : "aroundSignalId", "signal anchors cannot be combined with afterTs or cursor");
	const resolvedSemantics = resolveListSignalsReadQuerySemantics({
		recentOnly: state.recentOnly,
		hasExplicitLimit: value.hasLimit,
		limit: state.parsedLimit,
		afterSignalId: state.afterSignalId,
		aroundSignalId: state.aroundSignalId,
		beforeCount: state.beforeCount,
		afterCount: state.afterCount
	});
	if (!resolvedSemantics.ok) for (const issue of resolvedSemantics.issues) addQueryIssue(ctx, issue.path, issue.message);
};
/**
* Normalizes list-signals query into one execution shape.
* `hasLimit` keeps cursor-response mode explicit even without cursor/afterTs.
*/
const listSignalsQuerySchema = listSignalsQueryInputSchema.superRefine((value, ctx) => {
	if (value.q && (value.afterTs || value.recent || value.afterSignalId || value.aroundSignalId || value.fromProfileIds.length || value.signalTypes.length || value.cursor && !spaceMessageSearchQuerySchema.shape.cursor.safeParse(value.cursor).success)) ctx.addIssue({
		code: "custom",
		path: ["q"],
		message: "Message search cannot be combined with timeline filters."
	});
	const state = buildListSignalsQueryValidationState(value);
	validateListSignalsPrimitiveValues(value, state, ctx);
	validateListSignalsSignalTypes(value.signalTypes, ctx);
	validateListSignalsQueryCombinations(value, state, ctx);
}).transform((value) => {
	const afterTs = parseOptionalNonNegativeInt(value.afterTs);
	const cursor = normalizeCursor(value.cursor);
	const afterSignalId = normalizeIdentifier(value.afterSignalId);
	const aroundSignalId = normalizeIdentifier(value.aroundSignalId);
	const parsedLimit = parseOptionalNonNegativeInt(value.limit);
	const semantics = normalizeListSignalsReadQuerySemantics({
		recentOnly: parseRecentQuery(value.recent),
		hasExplicitLimit: value.hasLimit,
		limit: parsedLimit,
		afterSignalId,
		aroundSignalId,
		beforeCount: parseOptionalNonNegativeInt(value.beforeCount),
		afterCount: parseOptionalNonNegativeInt(value.afterCount)
	});
	const useCursorResponse = afterTs !== null || cursor !== null || value.hasLimit;
	return {
		...value.q ? { q: value.q } : {},
		afterTs,
		cursor,
		limit: semantics.limit,
		recentOnly: semantics.recentOnly,
		useCursorResponse,
		afterSignalId: semantics.afterSignalId,
		aroundSignalId: semantics.aroundSignalId,
		beforeCount: semantics.beforeCount,
		afterCount: semantics.afterCount,
		fromProfileIds: normalizeUniqueStrings(value.fromProfileIds),
		signalTypes: normalizeSignalTypes(value.signalTypes)
	};
});
//#endregion
export { listSignalsQuerySchema, normalizeListSignalsReadQuerySemantics, readListSignalsQueryRawValues, resolveListSignalsReadQuerySemantics };

//# sourceMappingURL=list-signals-query.js.map